import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Zap, LayoutDashboard, Users, FolderKanban, FileText, 
  CreditCard, Settings, LogOut, TrendingUp, UserPlus, FileEdit,
  ChevronRight, Plus, BarChart3, Globe, Search, Filter, FileDown,
  Sun, Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { adminAPI, aiAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import Logo from '../components/Logo';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const res = await adminAPI.getAnalytics();
        setAnalytics(res.data);
      }
      if (activeTab === 'leads' || activeTab === 'overview') {
        const res = await adminAPI.listLeads({});
        setLeads(res.data);
      }
      if (activeTab === 'clients' || activeTab === 'overview') {
        const res = await adminAPI.listClients({});
        setClients(res.data);
      }
      if (activeTab === 'projects' || activeTab === 'overview') {
        const res = await adminAPI.listProjects({});
        setProjects(res.data);
      }
      if (activeTab === 'invoices') {
        const res = await adminAPI.listInvoices({});
        setInvoices(res.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await adminAPI.updateLeadStatus(leadId, status);
      toast.success('Lead status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const convertLeadToClient = async (leadId) => {
    try {
      await adminAPI.convertLead(leadId);
      toast.success('Lead converted to client');
      loadData();
    } catch (error) {
      toast.error('Failed to convert lead');
    }
  };

  const downloadLeadProposalPDF = async (leadId, leadName) => {
    try {
      toast.info('Generating branded PDF proposal...');
      const response = await aiAPI.downloadProposalPDF(leadId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `XceedAgents-Proposal-${(leadName || 'Lead').replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: TrendingUp },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'projects', label: 'Projects', icon: FolderKanban },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'content', label: 'Content', icon: FileEdit },
  ];

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#050505] text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Sidebar */}
      <aside className={`w-64 border-r flex flex-col ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-white'}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={32} isDark={isDark} />
            <span className="font-heading font-bold">XceedAgents</span>
          </Link>
          <div className="mt-2 px-2 py-1 rounded bg-[#7000FF]/20 text-[#7000FF] text-xs font-mono">
            ADMIN
          </div>
          <Link 
            to="/admin/upwork" 
            className="mt-4 flex items-center gap-2 px-3 py-2 bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF] text-sm font-bold hover:bg-[#00F0FF]/20 transition-colors"
            data-testid="admin-upwork-link"
          >
            <Search className="w-4 h-4" />
            Upwork Scout
          </Link>
        </div>

        <nav className="flex-1 px-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                activeTab === tab.id 
                  ? 'bg-[#00F0FF]/10 text-[#00F0FF]' 
                  : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button
            onClick={toggleTheme}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg mb-3 transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="admin-theme-toggle"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className="text-sm">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7000FF] to-[#00F0FF]" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Super Admin</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className={`w-full justify-start ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className={`sticky top-0 z-10 backdrop-blur-sm border-b px-8 py-4 ${isDark ? 'bg-[#050505]/80 border-white/10' : 'bg-white/80 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold">
                {TABS.find(t => t.id === activeTab)?.label}
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Admin Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  className={`border rounded-lg pl-10 pr-4 py-2 text-sm focus:border-[#00F0FF] focus:outline-none w-64 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                />
              </div>
            </div>
          </div>
        </header>

        <div className="p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8"
                >
                  {/* Stats Grid */}
                  <div className="grid md:grid-cols-4 gap-6">
                    {[
                      { label: 'Total Leads', value: analytics?.total_leads || 0, icon: TrendingUp, color: '#00F0FF' },
                      { label: 'New Leads', value: analytics?.new_leads || 0, icon: UserPlus, color: '#00FF94' },
                      { label: 'Active Projects', value: analytics?.active_projects || 0, icon: FolderKanban, color: '#7000FF' },
                      { label: 'Total Revenue', value: formatCurrency(analytics?.total_revenue || 0), icon: CreditCard, color: '#FFD600', isText: true },
                    ].map((stat) => (
                      <Card key={stat.label} className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                          <span className="text-xs text-gray-500">{stat.label}</span>
                        </div>
                        <p className="text-3xl font-bold" style={{ color: stat.isText ? (isDark ? 'white' : '#111') : stat.color }}>
                          {stat.value}
                        </p>
                      </Card>
                    ))}
                  </div>

                  {/* Charts Row */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Leads by Country */}
                    <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#00F0FF]" />
                        Leads by Country
                      </h3>
                      <div className="space-y-3">
                        {(analytics?.leads_by_country || []).map((item) => (
                          <div key={item._id} className="flex items-center justify-between">
                            <span className="text-gray-400">{item._id || 'Unknown'}</span>
                            <span className="font-bold text-[#00F0FF]">{item.count}</span>
                          </div>
                        ))}
                        {(!analytics?.leads_by_country || analytics.leads_by_country.length === 0) && (
                          <p className="text-gray-500 text-sm">No data yet</p>
                        )}
                      </div>
                    </Card>

                    {/* Leads by Status */}
                    <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#7000FF]" />
                        Leads by Status
                      </h3>
                      <div className="space-y-3">
                        {(analytics?.leads_by_status || []).map((item) => (
                          <div key={item._id} className="flex items-center justify-between">
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(item._id)}`}>
                              {item._id || 'Unknown'}
                            </span>
                            <span className="font-bold">{item.count}</span>
                          </div>
                        ))}
                        {(!analytics?.leads_by_status || analytics.leads_by_status.length === 0) && (
                          <p className="text-gray-500 text-sm">No data yet</p>
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* Recent Items */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold">Recent Leads</h3>
                        <Button variant="ghost" onClick={() => setActiveTab('leads')}>
                          View All <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {leads.slice(0, 5).map((lead) => (
                          <Card key={lead.id} className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{lead.name}</p>
                                <p className="text-xs text-gray-500">{lead.company || lead.email}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(lead.status)}`}>
                                {lead.status}
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold">Active Projects</h3>
                        <Button variant="ghost" onClick={() => setActiveTab('projects')}>
                          View All <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {projects.filter(p => p.status === 'in_progress').slice(0, 5).map((project) => (
                          <Card key={project.id} className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{project.name}</p>
                                <p className="text-xs text-gray-500">{project.completion_percentage}% complete</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(project.status)}`}>
                                {project.status}
                              </span>
                            </div>
                          </Card>
                        ))}
                        {projects.filter(p => p.status === 'in_progress').length === 0 && (
                          <p className="text-gray-500 text-sm">No active projects</p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Leads Tab */}
              {activeTab === 'leads' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-400">{leads.length} total lead{leads.length !== 1 ? 's' : ''}</p>
                  </div>

                  {leads.length === 0 ? (
                    <Card className={`p-12 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">No Leads Yet</h3>
                      <p className="text-gray-500">Leads from the Start Your Project page will appear here with AI-generated proposals</p>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {leads.map((lead) => (
                        <Card key={lead.id} className={`overflow-hidden ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`} data-testid={`lead-card-${lead.id}`}>
                          {/* Lead Header */}
                          <div className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <h3 className="font-bold text-lg">{lead.name}</h3>
                                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(lead.status)}`}>
                                    {lead.status}
                                  </span>
                                  {lead.country && <span className={`text-xs px-2 py-1 ${isDark ? 'text-gray-500 bg-white/5' : 'text-gray-400 bg-gray-100'}`}>{lead.country}</span>}
                                  {lead.project_type && <span className="text-xs text-[#00F0FF]/80 bg-[#00F0FF]/10 px-2 py-1">{lead.project_type}</span>}
                                  {lead.industry && <span className="text-xs text-[#7000FF]/80 bg-[#7000FF]/10 px-2 py-1">{lead.industry}</span>}
                                </div>
                                <p className="text-sm text-gray-400 mb-1">{lead.email}{lead.company ? ` | ${lead.company}` : ''}</p>
                                {lead.budget_range && <p className="text-sm text-gray-500">Budget: <span className="text-[#00FF94]">{lead.budget_range}</span></p>}
                                {lead.timeline && <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Timeline: <span className={isDark ? 'text-white' : 'text-gray-900'}>{lead.timeline}</span></p>}
                                <p className="text-sm text-gray-500 mt-2">{lead.description?.substring(0, 200)}{lead.description?.length > 200 ? '...' : ''}</p>
                                
                                {/* AI Evaluation Summary */}
                                {lead.ai_evaluation && !lead.ai_evaluation.error && (
                                  <div className="mt-4 p-3 bg-[#00F0FF]/5 border border-[#00F0FF]/20">
                                    <p className="text-xs text-[#00F0FF] mb-2 font-semibold uppercase tracking-wider">AI Evaluation</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-500 block text-xs">Complexity</span>
                                        <span className="capitalize font-medium">{lead.ai_evaluation.complexity}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 block text-xs">Timeline</span>
                                        <span className="font-medium">{lead.ai_evaluation.estimated_timeline_weeks} weeks</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 block text-xs">Budget Est.</span>
                                        <span className="font-medium text-[#00FF94]">
                                          ${lead.ai_evaluation.estimated_budget_usd?.min?.toLocaleString()} - ${lead.ai_evaluation.estimated_budget_usd?.max?.toLocaleString()}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 block text-xs">AI Acceleration</span>
                                        <span className="font-medium">{lead.ai_evaluation.ai_acceleration_potential || 'N/A'}</span>
                                      </div>
                                    </div>
                                    {lead.ai_evaluation.recommended_stack && (
                                      <div className="mt-3">
                                        <span className="text-gray-500 text-xs">Stack: </span>
                                        {lead.ai_evaluation.recommended_stack.map((t, i) => (
                                          <span key={i} className={`text-xs px-2 py-0.5 mr-1 inline-block mt-1 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>{t}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="flex flex-col gap-2 ml-4 flex-shrink-0">
                                <select
                                  value={lead.status}
                                  onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                                  className={`border rounded px-3 py-1 text-sm ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                                  data-testid={`lead-status-select-${lead.id}`}
                                >
                                  <option value="new">New</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="qualified">Qualified</option>
                                  <option value="proposal">Proposal</option>
                                  <option value="negotiation">Negotiation</option>
                                  <option value="won">Won</option>
                                  <option value="lost">Lost</option>
                                </select>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-[#00F0FF]/30 text-[#00F0FF] text-xs"
                                  onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                                  data-testid={`lead-expand-btn-${lead.id}`}
                                >
                                  {expandedLead === lead.id ? 'Hide Details' : 'View Proposal'}
                                </Button>
                                {lead.status === 'won' && !lead.converted_to_client && (
                                  <Button 
                                    size="sm" 
                                    className="bg-[#00FF94] text-black text-xs"
                                    onClick={() => convertLeadToClient(lead.id)}
                                  >
                                    Convert to Client
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Proposal Detail */}
                          {expandedLead === lead.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className={`border-t ${isDark ? 'border-white/10 bg-[#0A0A12]' : 'border-gray-200 bg-gray-50'}`}
                            >
                              <div className="p-6 space-y-6">
                                {/* Full Description */}
                                <div>
                                  <h4 className="text-sm font-bold text-[#00F0FF] uppercase tracking-wider mb-2">Full Requirements</h4>
                                  <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{lead.description}</p>
                                </div>

                                {/* Features requested */}
                                {lead.features && (
                                  <div>
                                    <h4 className="text-sm font-bold text-[#00F0FF] uppercase tracking-wider mb-2">Features Requested</h4>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{lead.features}</p>
                                  </div>
                                )}

                                {/* AI Evaluation Details */}
                                {lead.ai_evaluation && !lead.ai_evaluation.error && (
                                  <div>
                                    <h4 className="text-sm font-bold text-[#00F0FF] uppercase tracking-wider mb-2">AI Evaluation Details</h4>
                                    {lead.ai_evaluation.key_features && (
                                      <div className="mb-3">
                                        <p className="text-xs text-gray-500 mb-1">Key Features Identified:</p>
                                        <ul className={`list-disc list-inside text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                          {lead.ai_evaluation.key_features.map((f, i) => <li key={i}>{f}</li>)}
                                        </ul>
                                      </div>
                                    )}
                                    {lead.ai_evaluation.risks && (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Risks:</p>
                                        <ul className="list-disc list-inside text-sm text-orange-300/80 space-y-1">
                                          {lead.ai_evaluation.risks.map((r, i) => <li key={i}>{r}</li>)}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* AI Proposal */}
                                {lead.ai_proposal && (
                                  <div>
                                    <h4 className="text-sm font-bold text-[#7000FF] uppercase tracking-wider mb-2">AI-Generated Proposal</h4>
                                    <div className={`border p-4 max-h-96 overflow-y-auto ${isDark ? 'bg-[#0F0F18] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                      <pre className={`text-sm whitespace-pre-wrap font-sans leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lead.ai_proposal}</pre>
                                    </div>
                                  </div>
                                )}

                                {!lead.ai_proposal && (
                                  <div className="text-sm text-gray-500 italic">No AI proposal was generated for this lead.</div>
                                )}

                                {/* Metadata + PDF Download */}
                                <div className={`flex items-center justify-between pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                  <div className="flex items-center gap-6 text-xs text-gray-500">
                                    <span>Lead ID: {lead.id}</span>
                                    <span>Source: {lead.source}</span>
                                    <span>Created: {formatDate(lead.created_at)}</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-[#7000FF] text-white text-xs gap-1"
                                    onClick={() => downloadLeadProposalPDF(lead.id, lead.name)}
                                    data-testid={`lead-download-pdf-${lead.id}`}
                                  >
                                    <FileDown className="w-3.5 h-3.5" />
                                    Download PDF
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Clients Tab */}
              {activeTab === 'clients' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex justify-end">
                    <Button className="bg-[#00F0FF] text-black" data-testid="add-client-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Client
                    </Button>
                  </div>
                  
                  {clients.length === 0 ? (
                    <Card className={`p-12 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">No Clients Yet</h3>
                      <p className="text-gray-500">Convert leads or add clients manually</p>
                    </Card>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {clients.map((client) => (
                        <Card key={client.id} className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="font-bold">{client.company_name}</h3>
                              <p className="text-sm text-gray-500">{client.name}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(client.status)}`}>
                              {client.status}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-gray-500">Email:</span> {client.email}</p>
                            <p><span className="text-gray-500">Country:</span> {client.country}</p>
                            <p><span className="text-gray-500">Credits:</span> <span className="text-[#00F0FF]">{formatCurrency(client.credits)}</span></p>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex justify-end">
                    <Button className="bg-[#00F0FF] text-black" data-testid="add-project-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Project
                    </Button>
                  </div>
                  
                  {projects.length === 0 ? (
                    <Card className={`p-12 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <FolderKanban className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">No Projects Yet</h3>
                      <p className="text-gray-500">Create your first project</p>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {projects.map((project) => (
                        <Card key={project.id} className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-bold">{project.name}</h3>
                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(project.status)}`}>
                                  {project.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500 mb-4">{project.description}</p>
                              
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Type:</span>{' '}
                                  <span className="capitalize">{project.project_type?.replace('_', ' ')}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Budget:</span>{' '}
                                  {project.estimated_budget ? formatCurrency(project.estimated_budget) : 'TBD'}
                                </div>
                                <div>
                                  <span className="text-gray-500">Progress:</span>{' '}
                                  <span className="text-[#00F0FF]">{project.completion_percentage}%</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Started:</span>{' '}
                                  {formatDate(project.start_date) || 'TBD'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex justify-end">
                    <Button className="bg-[#00F0FF] text-black" data-testid="create-invoice-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Invoice
                    </Button>
                  </div>
                  
                  {invoices.length === 0 ? (
                    <Card className={`p-12 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">No Invoices Yet</h3>
                      <p className="text-gray-500">Create invoices for your clients</p>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {invoices.map((invoice) => (
                        <Card key={invoice.id} className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-bold">{invoice.invoice_number}</h3>
                              <p className="text-sm text-gray-500">
                                Due: {formatDate(invoice.due_date) || 'No due date'}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-2xl font-bold">{formatCurrency(invoice.total, invoice.currency)}</p>
                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(invoice.status)}`}>
                                  {invoice.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <h3 className="font-bold mb-4">Pages</h3>
                      <p className="text-gray-500 text-sm mb-4">Manage SEO pages, landing pages, and content</p>
                      <Button className="bg-[#00F0FF] text-black">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Page
                      </Button>
                    </Card>
                    
                    <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                      <h3 className="font-bold mb-4">Blog Posts</h3>
                      <p className="text-gray-500 text-sm mb-4">Manage blog articles and thought leadership</p>
                      <Button className="bg-[#7000FF] text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Post
                      </Button>
                    </Card>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
